<template>
  <div class="modal modal--filter modal--mobile fade" id="modalAdditionalAttributes" data-bs-keyboard="false" tabindex="-1" aria-labelledby="modalAdditionalAttributes" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header pt-4 pb-4 px-3 justify-content-center justify-content-sm-start mb-0">
          <!-- Close button - mobile -->
          <button type="button" class="btn-close d-sm-none" data-bs-dismiss="modal" aria-label="Close">
            <img src="/images/icons/ico-arrow-left.svg" alt="" class="icon icon--w32">
          </button>

          <div class="text-center">
            <h3 class="modal-title text-center mb-0 lh-1 d-none d-sm-block">{{ props.titleLong }}</h3>
            <h3 class="modal-title text-center mb-0 lh-1 d-sm-none">{{ props.titleShort }}</h3>
          </div>

          <!-- Close button - desktop -->
          <button type="button" class="btn-close d-none d-sm-block" data-bs-dismiss="modal" aria-label="Close">
            <img class="icon icon--close" src="/images/lynkac/icons/ico-close.svg" alt="">
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0">
          <div id="additionalFilter" class="additionalFilter d-flex" :data-active-group="activeGroupId">
            <!-- Left sidebar with attribute groups -->
            <div class="additionalFilter__controls">
              <ul class="list-unstyled m-0 p-0">
                <li
                  v-for="group in attributeGroups"
                  :key="group.id"
                  class="m-0 p-0"
                  :class="{ 'active': activeGroupId === group.id }"
                >
                  <button
                    class="additionalFilter__btn"
                    @click="activeGroupId = group.id"
                  >
                    {{ group.name }}
                  </button>
                </li>
              </ul>
            </div>

            <!-- Right content area with attributes -->
            <div class="additionalFilter__content">
              <div
                v-for="group in attributeGroups"
                :key="group.id"
                :id="`additionalFilterGroup-${group.id}`"
                class="additionalFilter__content-item"
                :class="{ 'd-none': activeGroupId !== group.id, 'active': activeGroupId === group.id }"
              >
                <!-- Mobile header -->
                <div class="modal-header d-sm-none pt-4 pb-4 px-3 justify-content-center justify-content-sm-start mb-0">
                  <button type="button" class="btn-close" @click="activeGroupId = null">
                    <img src="/images/icons/ico-arrow-left.svg" alt="" class="icon icon--w32">
                  </button>
                  <div class="text-center">
                    <h2 class="modal-title mb-0 lh-1">{{ group.name }}</h2>
                  </div>
                </div>

                <!-- Attributes for this group -->
                <div class="additionalFilter__content-item-body">
                  <div>
                    <template v-for="attribute in getAttributesForGroup(group.id)" :key="attribute.id">
                      <!-- Boolean type attributes -->
                      <div v-if="attribute.type === 'boolean'" class="form-check">
                        <input
                          type="checkbox"
                          :id="`filter--additional-attributes-${attribute.id}`"
                          :value="true"
                          v-model="localFilters[attribute.id]"
                          :disabled="!getAttributeCount(attribute)"
                          class="form-check-input"
                        >
                        <label
                          :for="`filter--additional-attributes-${attribute.id}`"
                          class="form-check-label"
                          :class="{ 'text--stroke--modal': !getAttributeCount(attribute) }"
                        >
                          {{ attribute.name }} ({{ getAttributeCount(attribute) }})
                        </label>
                      </div>

                      <!-- Array type attributes -->
                      <div v-else-if="attribute.type === 'array'" class="my-3">
                        <label
                          :for="`filter--additional-attributes-${attribute.id}`"
                          class="d-block text--semi-small text--grey-dark text--500 lh-1 mb-2"
                        >
                          {{ attribute.name }}
                        </label>
                        <select
                          :id="`filter--additional-attributes-${attribute.id}`"
                          v-model="localFilters[attribute.id]"
                          class="form-select btn-sm-on-mobile mb-1"
                          :aria-label="attribute.name"
                        >
                          <option value="">-</option>
                          <option
                            v-for="option in attribute.options"
                            :key="option.id"
                            :value="option.id"
                            :disabled="!getOptionCount(option)"
                          >
                            {{ option.name }} ({{ getOptionCount(option) }})
                          </option>
                        </select>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Mobile footer -->
                <div class="modal-footer d-sm-none">
                  <button
                    class="btn btn-outline-primary me-3"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    {{ props.btnCancel }}
                  </button>
                  <button
                    @click="applyFilters"
                    class="btn btn-primary"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    {{ props.btnSave }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop footer -->
        <div class="modal-footer d-none d-sm-flex">
          <button
            class="btn btn-outline-primary me-3"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            {{ props.btnCancel }}
          </button>
          <button
            @click="applyFilters"
            class="btn btn-primary"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            {{ props.btnSave }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';

const props = defineProps({
  attributes: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Object,
    default: () => ({})
  },
  titleLong: {
    type: String,
    required: true
  },
  titleShort: {
    type: String,
    required: true
  },
  btnCancel: {
    type: String,
    required: true
  },
  btnSave: {
    type: String,
    required: true
  },
  counts: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'apply']);

// Local copy of filters that we'll modify and then emit on apply
const localFilters = ref({...props.modelValue});

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  localFilters.value = {...newValue};
}, { deep: true });

// Extract unique attribute groups from the attributes
const attributeGroups = computed(() => {
  const groups = [];
  const groupIds = new Set();

  props.attributes.forEach(attr => {
    if (attr.group && !groupIds.has(attr.group.id)) {
      groupIds.add(attr.group.id);
      groups.push(attr.group);
    }
  });

  return groups;
});

// Track the active group
// On desktop, we start with the first group selected
// On mobile, we start with no group selected (showing the group list)
const isMobile = ref(window.innerWidth < 576);
const activeGroupId = ref(isMobile.value ? null : (attributeGroups.value.length > 0 ? attributeGroups.value[0].id : null));

// Update isMobile on window resize
onMounted(() => {
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth < 576;
  });
});

// Get attributes for a specific group
function getAttributesForGroup(groupId) {
  return props.attributes.filter(attr => attr.group && attr.group.id === groupId);
}

// Apply the filters and close the modal
function applyFilters() {
  emit('update:modelValue', {...localFilters.value});
  emit('apply');
}

// Helper function to get attribute count with fallback
function getAttributeCount(attribute) {
  // For boolean attributes, return the count directly
  if (attribute.type === 'boolean') {
    return props.counts[attribute.id] || 0;
  }

  // For array attributes, sum up the counts of all options
  if (attribute.type === 'array' && attribute.options && attribute.options.length) {
    const optionCounts = props.counts[attribute.id] || {};
    const sum = Object.values(optionCounts).reduce((total, count) => total + (count || 0), 0);
    return sum;
  }

  // Default fallback
  return 0;
}

// Helper function to get option count with fallback
function getOptionCount(option) {
  // Find the parent attribute
  const attribute = props.attributes.find(attr =>
    attr.options && attr.options.some(opt => opt.id === option.id)
  );

  if (attribute && props.counts[attribute.id]) {
    return props.counts[attribute.id][option.id] || 0;
  }

  return 0;
}

// Initialize Bootstrap modal
let modal;
onMounted(() => {
  // Import Bootstrap's Modal class
  import('bootstrap').then(bootstrap => {
    const modalElement = document.getElementById('modalAdditionalAttributes');
    modal = new bootstrap.Modal(modalElement);

    // Add event listeners to handle body class for z-index management
    modalElement.addEventListener('show.bs.modal', () => {
      document.body.classList.add('modal-filter-active');
    });

    modalElement.addEventListener('hidden.bs.modal', () => {
      document.body.classList.remove('modal-filter-active');
    });
  });
});

// Expose methods to parent component
defineExpose({
  show() {
    // On desktop, set the first group as active
    // On mobile, don't select any group initially (show the group list)
    if (attributeGroups.value.length > 0) {
      activeGroupId.value = isMobile.value ? null : attributeGroups.value[0].id;
    }
    if (modal) modal.show();
  },
  hide() {
    if (modal) modal.hide();
  }
});
</script>

<style scoped>
.additionalFilter {
  display: flex;
}

.additionalFilter__controls {
  width: 30%;
  border-right: 1px solid #dee2e6;
}

.additionalFilter__content {
  width: 70%;
}

.additionalFilter__btn {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #e6e6e6;
}

.additionalFilter__controls li.active .additionalFilter__btn {
  background-color: #1ef1c6;
  color: #000;
  font-weight: bold;
}

.additionalFilter__content-item-body {
  padding: 1rem;
}

@media (max-width: 576px) {
  .additionalFilter__controls {
    width: 100%;
    border-right: none;
  }

  .additionalFilter__content {
    width: 100%;
  }

  .additionalFilter {
    flex-direction: column;
  }

  /* Hide controls when a group is active */
  .additionalFilter__controls {
    display: block;
  }

  /* Hide controls and show content when a group is selected */
  .additionalFilter[data-active-group] .additionalFilter__controls {
    display: none;
  }

  /* Ensure content is visible when a group is selected */
  .additionalFilter__content-item {
    display: none;
  }

  .additionalFilter__content-item.active {
    display: block;
  }
}

/* Fix for modal-backdrop covering modal content when opened within filter sidebar */
:deep(body.modal-filter-active .modal-backdrop) {
  z-index: 1040 !important;
  pointer-events: none !important;
  opacity: 0 !important;
}

:deep(body.modal-filter-active .modal) {
  z-index: 1050 !important;
  pointer-events: auto !important;
}

/* Ensure modal content is above backdrop */
:deep(body.modal-filter-active .modal-content) {
  position: relative;
  z-index: 1060 !important;
  pointer-events: auto !important;
}
</style>
